<template>
  <div class="pr-10 mb-4">
    <!-- {{ weekOffset }} -->
    <v-row class="align-center">
      <v-col cols="auto">
        <v-select
          :model-value="selectedYear"
          :items="yearOptions"
          label="Año"
          variant="outlined"
          density="compact"
          style="width: 120px"
          @update:model-value="handleYearChange"
        ></v-select>
      </v-col>
      <v-col style="max-width: calc(100% - 30px)">
        <v-slider
          v-model="currentWeek"
          :min="1"
          :max="53"
          :step="1"
          thumb-label
          @update:modelValue="validateWeekChange"
        >
          <template v-slot:thumb-label="{ modelValue }">
            <div class="thumb-label">Semana {{ modelValue }}</div>
          </template>
        </v-slider>
      </v-col>
      <v-col cols="auto">
        <v-btn
          @click="goToToday"
          color="primary"
          :disabled="isTodayInVisibleRange"
        >
          Hoy
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <!-- :color="previsionMode ? 'warning' : 'default'" -->
        <v-btn
          v-if="showSaveButton"
          color="default"
          :class="{ 'v-btn--active': previsionMode }"
          @click="togglePrevisionMode"
        >
          <v-icon start>{{
            previsionMode ? 'mdi-eye' : 'mdi-eye-outline'
          }}</v-icon>
          Previsión
        </v-btn>
      </v-col>
      <v-col cols="auto" v-if="showSaveButton">
        <v-btn
          @click="$emit('export')"
          color="secondary"
          :disabled="!turnosChanged"
        >
          Guardar cambios
        </v-btn>
      </v-col>
    </v-row>
    <v-dialog v-model="showConfirmDialog" max-width="400">
      <v-card>
        <v-card-text class="pt-4">
          Si continúas se perderán los cambios realizados
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="cancelWeekChange">
            Cancelar
          </v-btn>
          <v-btn color="primary" @click="confirmWeekChange"> Continuar </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { getISOWeekNumber, getTodayDate } from '@/utils/helpers'

function getMondayOfWeek(weekNumber, year) {
  // Asegurarse de que weekNumber sea válido
  const validWeekNumber = Math.max(1, Math.min(53, weekNumber || 1))

  const firstDayOfYear = new Date(year, 0, 1)
  const firstDayOfWeek = firstDayOfYear.getDay()
  const daysToFirstThursday = (11 - firstDayOfWeek) % 7
  const firstThursday = new Date(year, 0, 1 + daysToFirstThursday)
  const firstMonday = new Date(firstThursday)
  firstMonday.setDate(firstThursday.getDate() - 3)
  const targetMonday = new Date(firstMonday)
  targetMonday.setDate(firstMonday.getDate() + (validWeekNumber - 1) * 7)

  return targetMonday
}

const props = defineProps({
  weekOffset: {
    type: Number,
    required: true
  },
  selectedYear: {
    type: Number,
    required: true
  },
  turnosChanged: {
    type: Boolean,
    required: true
  },
  showSaveButton: {
    type: Boolean,
    default: true
  },
  selectedUserId: {
    type: String,
    default: null
  }
})

const emit = defineEmits([
  'update:weekOffset',
  'export',
  'yearChange',
  'week-changed',
  'previsionModeChanged'
])

const currentYear = getTodayDate().getFullYear()
const selectedYear = computed({
  get: () => props.selectedYear,
  set: (value) => emit('yearChange', value)
})
const currentWeek = ref(1)

const yearOptions = computed(() => {
  return Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)
})

const handleYearChange = (year) => {
  if (props.turnosChanged) {
    previousYear.value = selectedYear.value
    pendingYear.value = year
    pendingAction.value = 'year'
    showConfirmDialog.value = true
    return
  }
  selectedYear.value = year
  emit('yearChange', year)
}

const goToToday = () => {
  if (props.turnosChanged) {
    pendingAction.value = 'today'
    showConfirmDialog.value = true
    return
  }
  executeGoToToday()
}

const executeGoToToday = async () => {
  const today = getTodayDate()

  console.log(
    '📅 executeGoToToday - Fecha simulada:',
    today.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  )

  const newWeek = Math.max(1, getISOWeekNumber(today))

  // Calcular el año ISO de la semana
  // En ISO 8601, las semanas al final/inicio del año pueden pertenecer al año siguiente/anterior
  const tempDate = new Date(today)
  tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7))
  const newYear = tempDate.getFullYear()

  console.log('📊 Año ISO calculado:', newYear, '| Semana ISO:', newWeek)

  // Primero actualizamos el año
  selectedYear.value = newYear
  emit('yearChange', newYear)

  // Luego actualizamos la semana
  currentWeek.value = newWeek
  emit('update:weekOffset', newWeek)
  emit('week-changed', newWeek)
}

const isTodayInVisibleRange = computed(() => {
  try {
    const today = getTodayDate()
    today.setHours(0, 0, 0, 0)

    // Asegurarse de que tenemos valores válidos
    const currentWeekOffset = props.weekOffset || 1
    const currentYear = selectedYear.value || getTodayDate().getFullYear()

    // Usar getMondayOfWeek para obtener el primer día del rango visible
    const startDate = getMondayOfWeek(currentWeekOffset, currentYear)

    // Calcular el último día (28 días después del primer día)
    const lastDay = new Date(startDate)
    lastDay.setDate(startDate.getDate() + 27)

    return today >= startDate && today <= lastDay
  } catch (error) {
    console.error('Error en isTodayInVisibleRange:', error)
    return false
  }
})

// Variables para el diálogo de confirmación
const showConfirmDialog = ref(false)
const pendingWeekValue = ref(null)
const pendingAction = ref(null)
const pendingYear = ref(null)
const previousYear = ref(null)

const validateWeekChange = (newValue) => {
  if (props.turnosChanged) {
    pendingWeekValue.value = newValue
    pendingAction.value = 'week'
    showConfirmDialog.value = true
    return
  }
  onWeekChange(newValue)
}

const onWeekChange = (newValue) => {
  emit('update:weekOffset', newValue)
  emit('week-changed', newValue)
}

const confirmWeekChange = () => {
  switch (pendingAction.value) {
    case 'week':
      onWeekChange(pendingWeekValue.value)
      break
    case 'year':
      selectedYear.value = pendingYear.value
      emit('yearChange', pendingYear.value)
      break
    case 'today':
      executeGoToToday()
      break
  }
  showConfirmDialog.value = false
}

const cancelWeekChange = () => {
  switch (pendingAction.value) {
    case 'week':
      currentWeek.value = props.weekOffset
      break
    case 'year':
      // No necesitamos hacer nada aquí porque el v-select
      // seguirá mostrando selectedYear.value que nunca cambió
      break
  }
  showConfirmDialog.value = false
}

// Observar cambios en weekOffset para actualizar el slider
watch(
  () => props.weekOffset,
  (newOffset) => {
    currentWeek.value = newOffset
  },
  { immediate: true }
)

// Observar cambios en selectedYear para mantener sincronizado el v-select
watch(
  () => props.selectedYear,
  (newYear) => {
    selectedYear.value = newYear
  },
  { immediate: true }
)

onMounted(() => {
  const today = getTodayDate()
  currentWeek.value = Math.max(1, getISOWeekNumber(today))
  console.log(
    '🧪 CalendarNavigation onMounted - Semana inicial:',
    currentWeek.value
  )
})

const previsionMode = ref(false)

const togglePrevisionMode = () => {
  previsionMode.value = !previsionMode.value
  emit('previsionModeChanged', previsionMode.value)
}
</script>

<style scoped>
.thumb-label {
  padding: 0 8px;
  min-width: 70px;
  text-align: center;
  white-space: nowrap;
  overflow: visible;
}

/* Asegurarse de que el contenedor del tooltip tenga suficiente espacio */
:deep(.v-slider-thumb__label) {
  min-width: 90px;
  height: auto !important;
  padding: 4px 8px;
}

.year-select {
  width: 120px;
}

.v-btn--active {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: white !important;
}
</style>
